import { Minus, Plus } from "lucide-react";
import { Button } from "./Button";
import { cn } from "../lib/cn";

export interface QuantityStepperProps {
  qty: number;
  availableQty?: number;
  disabled?: boolean;
  onAdd: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  size?: "sm" | "md" | "lg";
  addLabel?: string;
  className?: string;
}

export function QuantityStepper({
  qty,
  availableQty = 9999,
  disabled = false,
  onAdd,
  onIncrement,
  onDecrement,
  size = "md",
  addLabel = "Add",
  className,
}: QuantityStepperProps) {
  if (qty === 0) {
    return (
      <Button
        type="button"
        variant="primary"
        size={size === "lg" ? "lg" : size === "sm" ? "sm" : "md"}
        disabled={disabled || availableQty <= 0}
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        className={cn(
          "w-full font-bold shadow-2xs bg-cta text-cta-foreground hover:opacity-95 transition-opacity",
          size === "sm" ? "h-8 text-xs" : size === "lg" ? "h-11 text-sm" : "h-9 text-xs sm:text-sm",
          className
        )}
      >
        <Plus className="h-4 w-4 mr-1 shrink-0" /> {availableQty <= 0 ? "Out of Stock" : addLabel}
      </Button>
    );
  }

  const heightClass = {
    sm: "h-8",
    md: "h-9",
    lg: "h-11 min-h-[44px]",
  }[size];

  const buttonClass = {
    sm: "h-6 w-7",
    md: "h-7 w-8",
    lg: "h-10 w-10 min-h-[40px] min-w-[40px]",
  }[size];

  return (
    <div
      className={cn(
        "flex items-center justify-between w-full rounded-control bg-primary text-primary-foreground px-1 shadow-xs select-none",
        heightClass,
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDecrement();
        }}
        className={cn(
          "flex items-center justify-center rounded hover:bg-primary-foreground/15 active:scale-95 transition-all cursor-pointer",
          buttonClass
        )}
        aria-label="Decrease quantity"
      >
        <Minus className="h-4 w-4" />
      </button>

      <span className="text-xs sm:text-sm font-extrabold px-2 select-none type-num">
        {qty}
      </span>

      <button
        type="button"
        disabled={qty >= availableQty}
        onClick={(e) => {
          e.stopPropagation();
          onIncrement();
        }}
        className={cn(
          "flex items-center justify-center rounded hover:bg-primary-foreground/15 active:scale-95 disabled:opacity-40 transition-all cursor-pointer",
          buttonClass
        )}
        aria-label="Increase quantity"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
